import type {
  PlatformRole,
  WorkspacePermission,
  WorkspaceRoleSummary,
  WorkspaceStatus,
} from "@metaadsmanager/tk-master-contracts";

export type ProtectedRoute =
  | "account"
  | "admin"
  | "audit"
  | "invitation"
  /** TikTok ops / integrations — membership required, skip pause redirect. */
  | "pauseExempt"
  | "onboarding"
  | "paused"
  | "workspace";

export type RouteMembership = {
  permissions: WorkspacePermission[];
  role: WorkspaceRoleSummary;
  status: WorkspaceStatus;
};

type RouteAccessInput = {
  authenticated: boolean;
  membership: RouteMembership | null | undefined;
  platformRole: PlatformRole | null | undefined;
  requiredPermission?: WorkspacePermission;
  requiresWorkspaceOwner?: boolean;
  route: ProtectedRoute;
  /** 工作区身份跳转目标（无 dashboard.view 时不是 `/`）。 */
  workspaceHomeHref?: string;
};

export type RouteAccessDecision =
  | { action: "wait" }
  | { action: "allow" }
  | { action: "deny" }
  | { action: "redirect"; to: string };

/** 工作区概览落地（OSS 公网可读根 index.html）。 */
export const WORKSPACE_HOME_HREF = "/";

const AUTH_CALLBACK_TARGETS = new Set([
  "/",
  "/dashboard",
  "/auth/update-password",
]);

function homeHref(workspaceHomeHref: string | undefined): string {
  return workspaceHomeHref ?? WORKSPACE_HOME_HREF;
}

/**
 * 将认证状态、平台角色与唯一 Workspace 归属映射为门闸决策。
 * 缺权限为 deny（留在 URL）；redirect 只用于身份切换。
 */
export function resolveRouteAccess({
  authenticated,
  membership,
  platformRole,
  requiredPermission,
  requiresWorkspaceOwner,
  route,
  workspaceHomeHref,
}: RouteAccessInput): RouteAccessDecision {
  const workspaceHome = homeHref(workspaceHomeHref);
  if (!authenticated) return { action: "redirect", to: "/auth/login" };
  if (route === "account") return { action: "allow" };
  if (membership === undefined || platformRole === undefined) {
    return { action: "wait" };
  }

  if (route === "admin") {
    return platformRole === "super_admin"
      ? { action: "allow" }
      : { action: "redirect", to: workspaceHome };
  }

  if (platformRole === "super_admin") {
    return { action: "redirect", to: "/admin/workspaces" };
  }

  if (route === "invitation") {
    if (membership) return { action: "redirect", to: workspaceHome };
    return { action: "allow" };
  }

  if (route === "onboarding") {
    if (membership) return { action: "redirect", to: workspaceHome };
    return { action: "allow" };
  }

  if (!membership) {
    return { action: "redirect", to: "/onboarding" };
  }

  if (route === "paused") {
    return membership.status === "paused"
      ? { action: "allow" }
      : { action: "redirect", to: workspaceHome };
  }

  if (route === "audit") {
    if (
      requiredPermission &&
      !membership.permissions.includes(requiredPermission)
    ) {
      return membership.status === "paused"
        ? { action: "redirect", to: "/workspace/paused" }
        : { action: "deny" };
    }
    return { action: "allow" };
  }

  if (route === "pauseExempt") return { action: "allow" };

  if (membership.status === "paused") {
    return { action: "redirect", to: "/workspace/paused" };
  }

  if (requiresWorkspaceOwner && membership.role.kind !== "owner") {
    return { action: "deny" };
  }

  if (
    requiredPermission &&
    !membership.permissions.includes(requiredPermission)
  ) {
    return { action: "deny" };
  }

  return { action: "allow" };
}

/**
 * 将认证回调限制在已知站内页面，避免把查询参数直接交给客户端路由。
 * 旧 `/dashboard` 归一到公网可读的 `/`。
 */
export function resolveAuthCallbackTarget(target: string | null): string {
  if (!target || !AUTH_CALLBACK_TARGETS.has(target)) return WORKSPACE_HOME_HREF;
  if (target === "/dashboard") return WORKSPACE_HOME_HREF;
  return target;
}

/** 把 /v1/me 的 membership 行压成门闸输入。 */
export function toRouteMembership(membership: {
  permissions: WorkspacePermission[];
  role: WorkspaceRoleSummary;
  workspace: { status: WorkspaceStatus };
}): RouteMembership {
  return {
    permissions: membership.permissions,
    role: membership.role,
    status: membership.workspace.status,
  };
}
